import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";

export interface JobSummary {
  aircraft: { prod: string | null; worker1: string | null; worker2: string | null } | null;
  obstacles: { prod: string | null; worker1: string | null; worker2: string | null } | null;
  towing: { prod: string | null; worker1: string | null; worker2: string | null } | null;
}

/**
 * Type guard to validate JobSummary data at runtime
 * Ensures data from backend matches expected structure
 */
export function isValidJobSummary(data: any): data is JobSummary {
  if (!data || typeof data !== 'object') return false;

  // Check that each component is either null or an object with expected keys
  const validateComponent = (comp: any) => {
    if (comp === null) return true;
    if (typeof comp !== 'object') return false;
    return (
      ('prod' in comp) &&
      ('worker1' in comp) &&
      ('worker2' in comp)
    );
  };

  return (
    validateComponent(data.aircraft) &&
    validateComponent(data.obstacles) &&
    validateComponent(data.towing)
  );
}

const StatusCell: React.FC<{ status: string | null }> = ({ status }) => {
  let symbol = "—";
  let color = "text.disabled";

  if (status === "success") {
    symbol = "✓";
    color = "success.main";
  } else if (status === "skipped") {
    symbol = "○";
    color = "info.main";
  } else if (status === "error") {
    symbol = "✗";
    color = "error.main";
  }

  return (
    <Box sx={{ color, fontWeight: "bold", fontSize: "1rem" }}>
      {symbol}
    </Box>
  );
};

export const ExecutionDetailPanel: React.FC<{ summary: JobSummary | null }> = ({ summary }) => {
  const { t } = useTranslation();

  if (!summary) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {t('settings.uberAdmin.executionMatrix.noDetails')}
        </Typography>
      </Box>
    );
  }

  const components = [
    { key: "aircraft", label: t('settings.uberAdmin.executionMatrix.components.aircraft'), data: summary.aircraft },
    { key: "obstacles", label: t('settings.uberAdmin.executionMatrix.components.obstacles'), data: summary.obstacles },
    { key: "towing", label: t('settings.uberAdmin.executionMatrix.components.towingEquip'), data: summary.towing },
  ];

  return (
    <Box sx={{ p: 2, backgroundColor: "grey.50" }}>
      <Typography variant="subtitle2" gutterBottom>
        {t('settings.uberAdmin.executionMatrix.title')}
      </Typography>
      <Table size="small" sx={{ maxWidth: 400 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: "bold" }}>{t('settings.uberAdmin.executionMatrix.headers.component')}</TableCell>
            <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>{t('settings.uberAdmin.executionMatrix.headers.prod')}</TableCell>
            <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>{t('settings.uberAdmin.executionMatrix.headers.worker1')}</TableCell>
            <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>{t('settings.uberAdmin.executionMatrix.headers.worker2')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {components.map(({ key, label, data }) => (
            <TableRow key={key}>
              <TableCell>{label}</TableCell>
              <TableCell sx={{ textAlign: "center" }}>
                <StatusCell status={data?.prod || null} />
              </TableCell>
              <TableCell sx={{ textAlign: "center" }}>
                <StatusCell status={data?.worker1 || null} />
              </TableCell>
              <TableCell sx={{ textAlign: "center" }}>
                <StatusCell status={data?.worker2 || null} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Box sx={{ mt: 1, display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Typography variant="caption" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box component="span" sx={{ color: "success.main", fontWeight: "bold" }}>✓</Box>
          {t('settings.uberAdmin.executionMatrix.legend.success')}
        </Typography>
        <Typography variant="caption" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box component="span" sx={{ color: "info.main", fontWeight: "bold" }}>○</Box>
          {t('settings.uberAdmin.executionMatrix.legend.skipped')}
        </Typography>
        <Typography variant="caption" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box component="span" sx={{ color: "error.main", fontWeight: "bold" }}>✗</Box>
          {t('settings.uberAdmin.executionMatrix.legend.error')}
        </Typography>
        <Typography variant="caption" sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "text.disabled" }}>
          <Box component="span" sx={{ fontWeight: "bold" }}>—</Box>
          {t('settings.uberAdmin.executionMatrix.legend.na')}
        </Typography>
      </Box>
    </Box>
  );
};
